import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';

import { useAppSelector } from '@/app/store/hooks';
import {
  selectPlayers,
  selectRollsStatus,
  selectSpinTargetAngle,
  selectTotalPot,
} from '@/entities/rolls';
import { RollsPlayer, RollsStatus } from '@/entities/rolls';
import { GAME_CONFIG } from '@/shared/config/constants';
import { formatUsd } from '@/shared/lib/currency';

import { usePointerTracking } from './usePointerTracking';
import type { WheelSegment } from './useWheelSegments';
import { useWheelSegments } from './useWheelSegments';

// Convert raw proportional angle (winning_ticket / total_pot * 360) to the
// visual angle on the disc, accounting for min-angle enforcement in segments.
function rawToVisualAngle(
  rawAngle: number,
  players: RollsPlayer[],
  segments: WheelSegment[],
): number {
  if (segments.length === 0) return rawAngle;
  const totalBet = players.reduce((s, p) => s + p.betAmount, 0);
  if (totalBet === 0) return rawAngle;

  const winningTon = (rawAngle / 360) * totalBet;
  let cumulative = 0;
  for (let i = 0; i < segments.length; i++) {
    const bet = players[i].betAmount;
    if (winningTon < cumulative + bet || i === segments.length - 1) {
      const frac = bet > 0 ? Math.max(0, Math.min(1, (winningTon - cumulative) / bet)) : 0.5;
      return segments[i].startAngle + frac * segments[i].angle;
    }
    cumulative += bet;
  }
  return rawAngle;
}
import './PvpWheel.scss';

const WHEEL_SIZE = 300;
const RADIUS = WHEEL_SIZE / 2;
const CENTER = RADIUS;
const SEGMENT_RADIUS = RADIUS / 2;
const CIRCUMFERENCE = 2 * Math.PI * SEGMENT_RADIUS;

const SEGMENT_TRANSITION_STYLE = 'stroke-dasharray 0.7s cubic-bezier(0.4, 0, 0.2, 1)';

const IDLE_SEGMENT_COUNT = 12;
const IDLE_COLORS = ['#3a3f5c', '#2a2e45'] as const;

// Reveals a wheel segment by transitioning stroke-dasharray from an initial
// (empty or full) value to its final length, mimicking a CSS mount-in.
function SegmentCircle({
  color,
  initialDash,
  finalDash,
}: {
  color: string;
  initialDash: string;
  finalDash: string;
}) {
  const [dash, setDash] = useState(initialDash);
  const isMountRef = useRef(true);

  // Mount-only: start from initialDash, then transition to finalDash. Later
  // finalDash changes (e.g. bet amounts shifting) animate from the current
  // value via the CSS transition, matching framer-motion's animate-on-update.
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setDash(finalDash));
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    setDash(finalDash);
  }, [finalDash]);

  return (
    <circle
      cx={CENTER}
      cy={CENTER}
      r={SEGMENT_RADIUS}
      fill="none"
      stroke={color}
      strokeWidth={RADIUS}
      strokeDasharray={dash}
      opacity={0.88}
      style={{ transition: SEGMENT_TRANSITION_STYLE }}
    />
  );
}

// Positions a player avatar on the wheel; left/top reposition smoothly on
// segment changes, while opacity/scale run a one-time mount-in fade+pop.
function SegmentAvatar({
  left,
  top,
  size,
  children,
}: {
  left: number;
  top: number;
  size: number;
  children: React.ReactNode;
}) {
  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className={`pvp-wheel__segment-avatar${mounted ? ' pvp-wheel__segment-avatar--in' : ''}`}
      style={{
        position: 'absolute',
        width: size,
        height: size,
        left,
        top,
        transform: 'translate(-50%, -50%)',
      }}>
      {children}
    </div>
  );
}

interface PvpWheelProps {
  onCurrentPlayer?: (player: RollsPlayer | null) => void;
}

export function PvpWheel({ onCurrentPlayer }: PvpWheelProps) {
  const players = useAppSelector(selectPlayers);
  const spinTargetAngle = useAppSelector(selectSpinTargetAngle);
  const gameStatus = useAppSelector(selectRollsStatus);
  const totalPot = useAppSelector(selectTotalPot);

  const isSpinning = gameStatus === RollsStatus.SPINNING;
  const isFinished = gameStatus === RollsStatus.FINISHED;

  const segments = useWheelSegments(players, CIRCUMFERENCE);
  const handleDiscUpdate = usePointerTracking(segments, isSpinning, onCurrentPlayer);
  const discRef = useRef<HTMLDivElement>(null);

  // Persist rotation across SPINNING → FINISHED
  const finalRotationRef = useRef(0);
  if (gameStatus === RollsStatus.WAITING) {
    finalRotationRef.current = 0;
  }
  if (isSpinning && spinTargetAngle !== null) {
    // Convert raw proportional angle → visual angle so the arrow lands precisely
    // on the winner's visual segment (segments may differ from raw due to min-angle enforcement)
    const visualAngle = rawToVisualAngle(spinTargetAngle, players, segments);
    finalRotationRef.current = 360 * 5 + (360 - visualAngle);
  }
  const targetRotation = isSpinning || isFinished ? finalRotationRef.current : 0;

  // Poll the live CSS-transitioned rotation each frame while spinning so
  // usePointerTracking can tell which segment sits under the pointer.
  useEffect(() => {
    if (!isSpinning) return;
    let raf = 0;
    const tick = () => {
      const node = discRef.current;
      if (node) {
        const transform = getComputedStyle(node).transform;
        let rotate = 0;
        if (transform && transform !== 'none') {
          const matrix = new DOMMatrix(transform);
          rotate = (Math.atan2(matrix.b, matrix.a) * 180) / Math.PI;
          if (rotate < 0) rotate += 360;
        }
        handleDiscUpdate({ rotate });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isSpinning, handleDiscUpdate]);

  // Render bottom-to-top: last player at bottom (first in SVG), first player on top (last in SVG)
  // When existing circles shrink, they reveal the new circle underneath = "split" effect
  const renderOrder = [...segments].reverse();
  const isIdle = segments.length === 0;

  return (
    <div className="pvp-wheel">
      <div className="pvp-wheel__container" style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}>
        {/* Glow */}
        <div className={`pvp-wheel__glow${isSpinning ? ' pvp-wheel__glow--spinning' : ''}`} />

        {/* Pointer */}
        <div className="pvp-wheel__pointer-wrap">
          <div className="pvp-wheel__pointer" />
        </div>

        {/* Spinning disc */}
        <div
          ref={discRef}
          className={`pvp-wheel__disc${isIdle ? ' pvp-wheel__disc--idle' : ''}`}
          style={
            isIdle
              ? undefined
              : {
                  transform: `rotate(${targetRotation}deg)`,
                  transitionDuration: `${isSpinning ? GAME_CONFIG.ROLLS.SPIN_DURATION : 0}s`,
                  transitionTimingFunction: 'cubic-bezier(0.17, 0.67, 0.35, 0.96)',
                }
          }>
          <svg
            viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}
            width={WHEEL_SIZE}
            height={WHEEL_SIZE}
            className="pvp-wheel__svg"
            style={{ transform: 'rotate(-90deg)' }}>
            {/* Idle placeholder segments */}
            {isIdle &&
              [...Array(IDLE_SEGMENT_COUNT)]
                .map((_, i) => {
                  const segAngle = 360 / IDLE_SEGMENT_COUNT;
                  const cumLen = (((i + 1) * segAngle) / 360) * CIRCUMFERENCE;
                  return (
                    <circle
                      key={`idle-${i}`}
                      cx={CENTER}
                      cy={CENTER}
                      r={SEGMENT_RADIUS}
                      fill="none"
                      stroke={IDLE_COLORS[i % 2]}
                      strokeWidth={RADIUS}
                      strokeDasharray={`${cumLen} ${CIRCUMFERENCE}`}
                      opacity={0.88}
                    />
                  );
                })
                .reverse()}

            {renderOrder.map((seg, renderIndex) => {
              const isTopLayer = renderIndex === renderOrder.length - 1;
              return (
                <SegmentCircle
                  key={seg.id}
                  color={seg.color}
                  initialDash={
                    isTopLayer ? `0 ${CIRCUMFERENCE}` : `${CIRCUMFERENCE} ${CIRCUMFERENCE}`
                  }
                  finalDash={`${seg.cumDashLen} ${CIRCUMFERENCE}`}
                />
              );
            })}

            {/* Outer ring */}
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS - 2}
              fill="none"
              stroke="rgba(139, 92, 246, 0.5)"
              strokeWidth="3"
            />
          </svg>

          {/* Player avatars on segments */}
          {segments.map((seg) => {
            if (seg.angle < 14) return null;
            const midAngleDeg = seg.startAngle + seg.angle / 2 - 90;
            const rad = (midAngleDeg * Math.PI) / 180;
            const avatarR = RADIUS * 0.55;
            const x = CENTER + avatarR * Math.cos(rad);
            const y = CENTER + avatarR * Math.sin(rad);

            const size = seg.angle > 40 ? 32 : seg.angle > 20 ? 24 : 18;
            const offset = size / 2;

            return (
              <SegmentAvatar key={`avatar-${seg.id}`} left={x - offset} top={y - offset} size={size}>
                {seg.avatar ? (
                  <img
                    src={seg.avatar}
                    alt=""
                    className="pvp-wheel__segment-avatar-img"
                    style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                  />
                ) : (
                  <Jazzicon diameter={size} seed={jsNumberForAddress(seg.id)} />
                )}
              </SegmentAvatar>
            );
          })}

          {/* Center hub */}
          <div className="pvp-wheel__hub">
            <span className="pvp-wheel__hub-pot">
              {totalPot > 0 ? formatUsd(totalPot) : '$0.00'}
            </span>
            <span className="pvp-wheel__hub-label">USD</span>
          </div>
        </div>

        {/* Decorative dots */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`pvp-wheel__dot${isSpinning ? ' pvp-wheel__dot--spinning' : ''}`}
            style={
              {
                transform: `rotate(${i * 45}deg) translateY(-${RADIUS + 8}px) translateX(-50%)`,
                '--dot-delay': `${i * 0.1}s`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
}
